package music.jammers.client.shared.event.presentation.ui

import androidx.compose.foundation.gestures.animateScrollBy
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.rounded.ArrowBack
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowUp
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material3.Checkbox
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.unit.dp
import com.mohamedrejeb.compose.dnd.reorder.ReorderContainer
import com.mohamedrejeb.compose.dnd.reorder.ReorderableItem
import com.mohamedrejeb.compose.dnd.reorder.rememberReorderState
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import limasoul.client.shared.generated.resources.Res
import limasoul.client.shared.generated.resources.event_freeze_screen_backlog_header
import limasoul.client.shared.generated.resources.event_freeze_screen_save_button
import limasoul.client.shared.generated.resources.event_freeze_screen_switcher_label
import music.jammers.client.shared.component.BackgroundBox
import music.jammers.client.shared.component.ErrorDialog
import music.jammers.client.shared.component.JammersTopAppBar
import music.jammers.client.shared.component.Progress
import music.jammers.client.shared.component.track.presentation.ui.FreezeTrackCard
import music.jammers.client.shared.event.di.EventComponent
import music.limasoul.client.shared.event.presentation.viewmodel.EventFreezeItem
import music.limasoul.client.shared.event.presentation.viewmodel.EventFreezeState
import music.limasoul.client.shared.event.presentation.viewmodel.EventFreezeViewModel
import org.jetbrains.compose.resources.stringResource

const val EVENT_FREEZE_SCREEN = "EventFreezeScreen"

@Composable
fun EventFreezeScreen(eventId: Long) {
    val viewModel: EventFreezeViewModel = remember { EventComponent.eventFreezeViewModel.apply { onBind(eventId) } }
    val state = viewModel.state.value

    Scaffold(
        topBar = {
            JammersTopAppBar(
                text = state.title,
                navigationIcon = {
                    IconButton(
                        onClick = {
                            viewModel.onBackPressed()
                        }
                    ) {
                        val icon = if (state.selectMode) {
                            Icons.Filled.Close
                        } else {
                            Icons.AutoMirrored.Rounded.ArrowBack
                        }

                        Icon(
                            icon,
                            contentDescription = "Back",
                        )
                    }
                },
                actions = {
                    if (state.selectMode) {
                        IconButton(
                            onClick = {
                                viewModel.onMoveToSetClicked()
                            }
                        ) {
                            Icon(
                                Icons.Filled.KeyboardArrowUp,
                                contentDescription = "To set",
                            )
                        }

                        IconButton(
                            onClick = {
                                viewModel.onMoveToBacklogClicked()
                            }
                        ) {
                            Icon(
                                Icons.Filled.KeyboardArrowDown,
                                contentDescription = "To backlog",
                            )
                        }
                    } else {
                        IconButton(
                            onClick = {
                                viewModel.onSelectModeClicked()
                            }
                        ) {
                            Icon(
                                Icons.Filled.CheckCircle,
                                contentDescription = "Select mode"
                            )
                        }

                        IconButton(
                            onClick = {
                                viewModel.onUpdateClicked()
                            }
                        ) {
                            Icon(
                                Icons.Default.Refresh,
                                contentDescription = "Update set sorting",
                            )
                        }
                    }
                }
            )
        },
    ) { paddingValues ->
        BackgroundBox {
            if (state.progress) {
                Progress()
            } else {
                Column(
                    modifier = Modifier
                        .fillMaxHeight()
                        .padding(paddingValues)
                        .padding(16.dp),
                ) {
                    Content(
                        state = state,
                        viewModel = viewModel,
                    )
                }
            }

            state.error?.let { throwable ->
                ErrorDialog(message = throwable.message ?: throwable.toString()) {
                    viewModel.onErrorShown()
                }
            }
        }
    }
}

@Composable
private fun Content(
    state: EventFreezeState,
    viewModel: EventFreezeViewModel,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.fillMaxWidth(),
    ) {
        Text(
            text = stringResource(Res.string.event_freeze_screen_switcher_label),
            style = MaterialTheme.typography.bodyLarge,
        )

        Spacer(Modifier.weight(1f))

        Switch(
            checked = state.frozen,
            onCheckedChange = viewModel::onFrozenChanged,
        )
    }

    Column(
        Modifier.fillMaxHeight(),
    ) {
        Column(
            modifier = Modifier.weight(1f)
        ) {
            ReorderScreenContent(
                tracks = state.tracks,
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f)
                    .safeDrawingPadding(),
                isSelectMode = state.selectMode,
                onListUpdated = { eventTrackList ->
                    viewModel.onTrackListUpdated(eventTrackList)
                }
            )
        }

        Spacer(modifier = Modifier.size(8.dp))

        OutlinedButton(
            onClick = viewModel::onSaveClicked,
            modifier = Modifier.align(Alignment.CenterHorizontally)
        ) {
            Text(
                text = stringResource(Res.string.event_freeze_screen_save_button).uppercase(),
                style = MaterialTheme.typography.headlineSmall,
                modifier = Modifier.padding(horizontal = 64.dp),
            )
        }
    }
}

@Composable
private fun ReorderScreenContent(
    modifier: Modifier = Modifier,
    isSelectMode: Boolean,
    tracks: List<EventFreezeItem>,
    onListUpdated: (List<EventFreezeItem>) -> Unit,
) {
    val scope = rememberCoroutineScope()
    val reorderState = rememberReorderState<EventFreezeItem>(dragAfterLongPress = true)

    val lazyListState = rememberLazyListState()

    ReorderContainer(
        state = reorderState,
        modifier = modifier,
    ) {
        val dragItem: MutableState<EventFreezeItem?> = remember { mutableStateOf(null) }

        LazyColumn(
            state = lazyListState,
            modifier = Modifier
                .fillMaxWidth()
        ) {
            items(items = tracks) { item ->
                ReorderableItem(
                    state = reorderState,
                    key = item.id,
                    data = item,
                    onDrop = {
                        dragItem.value = null
                    },
                    onDragEnter = { state ->
                        dragItem.value = state.data

                        val updatedList = tracks.toMutableList().apply {
                            val index = indexOf(item)
                            if (index == -1) {
                                return@ReorderableItem
                            }

                            if (isSelectMode && item is EventFreezeItem.DataItem && item.checked) {
                                val selectedItems = filter { filterItem ->
                                    filterItem is EventFreezeItem.DataItem && filterItem.checked
                                }

                                removeAll(selectedItems)

                                if (index !in 0..size) {
                                    return@ReorderableItem
                                }

                                addAll(index, selectedItems)
                            } else {
                                remove(state.data)
                                add(index, state.data)
                            }

                            scope.launch {
                                handleLazyListScroll(
                                    lazyListState = lazyListState,
                                    dropIndex = index,
                                )
                            }
                        }

                        onListUpdated(updatedList)
                    },
                    draggableContent = {
                        DragItem(
                            item = dragItem.value ?: item,
                            isDragShadow = true,
                            onItemUpdated = { },
                            modifier = Modifier
                                .fillMaxWidth()
                        )
                    },
                    modifier = Modifier
                ) {
                    DragItem(
                        item = item,
                        onItemUpdated = { updatedItem ->
                            val updatedList = tracks.toMutableList().apply {
                                val index = indexOf(item)
                                if (index == -1) {
                                    return@apply
                                }

                                remove(item)
                                add(index, updatedItem)
                            }

                            onListUpdated(updatedList)
                        },
                        isSelectMode = isSelectMode,
                        modifier = Modifier
                            .graphicsLayer {
                                alpha = if (isDragging) 0f else 1f
                            }
                            .fillMaxWidth()
                    )
                }
            }
        }
    }
}

private suspend fun handleLazyListScroll(
    lazyListState: LazyListState,
    dropIndex: Int,
): Unit = coroutineScope {
    val firstVisibleItemIndex = lazyListState.firstVisibleItemIndex
    val firstVisibleItemScrollOffset = lazyListState.firstVisibleItemScrollOffset

    // Workaround to fix scroll issue when dragging the first item
    if (dropIndex == 0 || dropIndex == 1) {
        launch {
            lazyListState.scrollToItem(firstVisibleItemIndex, firstVisibleItemScrollOffset)
        }
    }

    // Animate scroll when entering the first or last item
    val lastVisibleItemIndex =
        lazyListState.firstVisibleItemIndex + lazyListState.layoutInfo.visibleItemsInfo.lastIndex

    val firstVisibleItem = lazyListState.layoutInfo.visibleItemsInfo.firstOrNull() ?: return@coroutineScope
    val scrollAmount = firstVisibleItem.size * 2f

    if (dropIndex <= firstVisibleItemIndex + 1) {
        launch {
            lazyListState.animateScrollBy(-scrollAmount)
        }
    } else if (dropIndex == lastVisibleItemIndex) {
        launch {
            lazyListState.animateScrollBy(scrollAmount)
        }
    }
}

@Composable
private fun DragItem(
    item: EventFreezeItem,
    isDragShadow: Boolean = false,
    isSelectMode: Boolean = false,
    onItemUpdated: (EventFreezeItem) -> Unit,
    modifier: Modifier = Modifier,
) {
    when (item) {
        is EventFreezeItem.BacklogHeader -> {
            Row(
                modifier.fillMaxWidth()
            ) {
                Text(
                    text = stringResource(Res.string.event_freeze_screen_backlog_header),
                    style = MaterialTheme.typography.headlineMedium,
                )
            }
        }

        is EventFreezeItem.DataItem      -> {
            Box {
                FreezeTrackCard(
                    eventTrack = item.eventTrack,
                    modifier = modifier
                        .then(
                            if (isDragShadow) {
                                Modifier
                                    .shadow(
                                        elevation = 20.dp,
                                        shape = RoundedCornerShape(12.dp),
                                    )
                            } else {
                                Modifier
                            }
                        )
                )

                if (isSelectMode) {
                    Checkbox(
                        checked = item.checked,
                        onCheckedChange = { checked ->
                            val updated = item.copy(checked = checked)
                            onItemUpdated(updated)
                        },
                        modifier = Modifier.align(Alignment.CenterEnd)
                    )
                }
            }
        }
    }
}